import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Lock, ExternalLink, Users, Loader2, LayoutGrid, ChevronDown } from "lucide-react";
import { api } from "@/api/client";
import { useI18n } from "@/i18n";
import { LoadError } from "@/components/LoadError";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function Directory() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const readSaved = (k, d) => { try { return JSON.parse(localStorage.getItem("vhrp_dir_filter") || "{}")[k] ?? d; } catch { return d; } };
  const [scope, setScope] = useState(() => readSaved("scope", "all"));
  const [search, setSearch] = useState("");
  const [city, setCity] = useState(() => readSaved("city", ""));
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [moreOpen, setMoreOpen] = useState(false);
  const [citySearch, setCitySearch] = useState("");

  const RAIL_MAX = 14;
  const cityStats = useMemo(() => data?.city_stats || [], [data]);
  const maxCount = cityStats[0]?.count || 1; // list is count-sorted; used for concentration bars
  const railStats = useMemo(() => {
    const head = cityStats.slice(0, RAIL_MAX);
    if (city && !head.some((s) => s.city === city)) {
      const sel = cityStats.find((s) => s.city === city);
      if (sel) head.unshift(sel);
    }
    return head;
  }, [cityStats, city]);
  const hasMore = cityStats.length > railStats.length;
  const citySections = useMemo(() => {
    const q = citySearch.trim().toLowerCase();
    const list = q ? cityStats.filter((c) => c.city.toLowerCase().includes(q)) : cityStats;
    const groups = new Map();
    for (const c of list) {
      const key = (c.state || "").trim() || t("dirOtherRegion");
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(c);
    }
    return [...groups.entries()]
      .map(([title, cities]) => ({ title, cities, total: cities.reduce((a, b) => a + b.count, 0) }))
      .sort((a, b) => b.total - a.total);
  }, [cityStats, citySearch, t]);

  // Remember the member's last-used filter (per device).
  useEffect(() => {
    try { localStorage.setItem("vhrp_dir_filter", JSON.stringify({ scope, city })); } catch { /* private mode */ }
  }, [scope, city]);

  const pickCity = (c) => {
    setCity((prev) => (prev === c ? "" : c));
    setMoreOpen(false);
    setCitySearch("");
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const qs = new URLSearchParams({ scope });
      if (city) qs.set("city", city);
      if (search) qs.set("search", search);
      const r = await api(`/directory?${qs.toString()}`);
      setData(r);
      setLocked(false);
    } catch (e) {
      if ((e.message || "").toLowerCase().includes("active")) setLocked(true);
      else setError(true);
    } finally {
      setLoading(false);
    }
  }, [scope, city, search]);

  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
  }, [load]);

  if (locked) {
    return (
      <div className="mx-auto max-w-md px-4 py-24 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-tint text-brand"><Lock size={28} /></div>
        <h1 className="mt-5 text-2xl font-black text-ink">{t("dirLockedTitle")}</h1>
        <p className="mt-2 text-ink-muted">{t("dirLockedSub")}</p>
        <Button data-testid="dir-join" onClick={() => navigate("/membership")} className="mt-6 h-12 rounded-full bg-brand px-8 font-bold hover:bg-brand-dark">
          {t("heroCta")}
        </Button>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10">
        <LoadError onRetry={load} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-tint text-brand"><Users size={22} /></span>
        <div>
          <h1 className="text-2xl font-black text-ink">{t("dirTile")}</h1>
          {data && <p className="text-sm text-ink-muted">{data.total_members} {t("dirTotalLabel")}</p>}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input
            data-testid="dir-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("dirSearchPh")}
            className="w-full rounded-full border border-line bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-brand"
          />
        </div>
        <div className="flex gap-2">
          {[["all", t("dirAll")], ["india", t("dirIndia")], ["international", t("dirIntl")]].map(([v, label]) => (
            <button
              key={v}
              data-testid={`dir-scope-${v}`}
              onClick={() => { setScope(v); setCity(""); }}
              className={`rounded-full px-4 py-2 text-sm font-semibold ${scope === v ? "bg-brand text-white" : "border border-line bg-white text-ink-soft"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {cityStats.length > 0 && (
        <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden" data-testid="dir-city-rail">
          <button
            data-testid="dir-city-all"
            onClick={() => setCity("")}
            className={`inline-flex h-9 flex-shrink-0 items-center gap-1.5 rounded-full border px-3.5 text-sm font-semibold transition-colors ${city === "" ? "border-brand bg-brand text-white" : "border-line bg-white text-ink-soft hover:border-brand"}`}
          >
            <LayoutGrid size={13} /> {t("dirCityAll")}
          </button>
          {railStats.map((cs) => {
            const on = city === cs.city;
            return (
              <button
                key={cs.city}
                data-testid={`dir-city-${cs.city}`}
                onClick={() => pickCity(cs.city)}
                className={`inline-flex h-9 flex-shrink-0 items-center gap-1.5 rounded-full border px-3.5 text-sm font-semibold transition-colors ${on ? "border-brand bg-brand text-white" : "border-line bg-white text-ink-soft hover:border-brand"}`}
              >
                <MapPin size={13} /> {cs.city}
                <span className={`inline-flex min-w-[18px] items-center justify-center rounded-full px-1 text-[11px] font-extrabold ${on ? "bg-white/25 text-white" : "bg-brand-tint text-brand"}`}>{cs.count}</span>
              </button>
            );
          })}
          {hasMore && (
            <button
              data-testid="dir-city-more"
              onClick={() => setMoreOpen(true)}
              className="inline-flex h-9 flex-shrink-0 items-center gap-1.5 rounded-full border border-brand/40 bg-brand-tint px-3.5 text-sm font-semibold text-brand transition-colors hover:bg-brand/10"
            >
              <ChevronDown size={13} /> {t("dirMoreCities")}
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>
      ) : data?.members?.length ? (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.members.map((m, i) => (
            <div key={`${m.full_name}-${m.city || ""}-${m.social_link || i}`} data-testid={`dir-member-${i}`} className="rounded-2xl border border-line bg-white p-5">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-tint font-black text-brand">
                {(m.full_name || "?")[0]}
              </div>
              <div className="mt-3 font-bold text-ink">{m.full_name}</div>
              <div className="text-sm text-brand">{m.designation}</div>
              <div className="mt-1 flex items-center gap-1 text-xs text-ink-muted">
                <MapPin size={12} /> {[m.city, m.state, m.country].filter(Boolean).join(", ")}
              </div>
              {m.social_link && /^https?:\/\//.test(m.social_link) && (
                <a href={m.social_link} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline">
                  <ExternalLink size={12} /> Link
                </a>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="py-20 text-center text-ink-muted">{t("dirEmpty")}</div>
      )}

      <Dialog open={moreOpen} onOpenChange={(o) => { setMoreOpen(o); if (!o) setCitySearch(""); }}>
        <DialogContent className="max-h-[80vh] max-w-md overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-left">{t("dirPickCity")}</DialogTitle>
            <DialogDescription className="sr-only">{t("dirCitySearchPh")}</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
            <input
              data-testid="dir-city-search"
              autoFocus
              value={citySearch}
              onChange={(e) => setCitySearch(e.target.value)}
              placeholder={t("dirCitySearchPh")}
              className="w-full rounded-full border border-line bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-brand"
            />
          </div>
          <div className="mt-2 max-h-[52vh] overflow-y-auto">
            {citySections.length ? citySections.map((sec) => (
              <div key={sec.title}>
                <div className="sticky top-0 flex items-center justify-between bg-white pb-1.5 pt-3">
                  <span className="text-[11px] font-extrabold uppercase tracking-wide text-ink-muted">{sec.title}</span>
                  <span className="text-[11px] font-bold text-brand">{sec.total}</span>
                </div>
                {sec.cities.map((cs) => {
                  const on = city === cs.city;
                  return (
                    <button
                      key={cs.city}
                      data-testid={`dir-city-opt-${cs.city}`}
                      onClick={() => pickCity(cs.city)}
                      className="flex w-full items-center gap-3 border-b border-line py-2.5 text-left"
                    >
                      <MapPin size={15} className={on ? "text-brand" : "text-ink-muted"} />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className={`text-sm ${on ? "font-extrabold text-brand" : "font-semibold text-ink"}`}>{cs.city}</span>
                          <span className="text-xs font-extrabold text-brand">{cs.count}</span>
                        </div>
                        <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-line">
                          <div className="h-1 rounded-full bg-brand" style={{ width: `${Math.max(6, (cs.count / maxCount) * 100)}%` }} />
                        </div>
                      </div>
                      {on && <span className="text-brand">✓</span>}
                    </button>
                  );
                })}
              </div>
            )) : (
              <p className="py-8 text-center text-sm text-ink-muted">{t("dirEmpty")}</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
